"use client"

import {
  REFERRAL_CODE_PARAM,
  REFERRAL_LS_CAPTURED_AT_KEY,
  REFERRAL_LS_KEY,
  getReferralAttributionWindowDays,
} from "@/lib/referral/constants"
import {
  buildReferralCodeSetCookieValue,
  normalizeReferralCode,
  readReferralCodeFromDocumentCookie,
} from "@/lib/referral/referral-code-cookie"
import {
  resolveReferralCapturedAtAtSignup,
  resolveReferralCodeAtSignup,
} from "@/lib/referral/resolve-signup-referral"

function isBrowser(): boolean {
  return typeof window !== "undefined" && typeof document !== "undefined"
}

function safeGetLocalStorage(): Storage | null {
  if (!isBrowser()) return null
  try {
    return window.localStorage
  } catch {
    return null
  }
}

function cookieMaxAgeSeconds(): number {
  return getReferralAttributionWindowDays() * 24 * 60 * 60
}

function persistReferralCodeCookie(code: string): void {
  if (!isBrowser()) return
  const secure = window.location.protocol === "https:"
  document.cookie = buildReferralCodeSetCookieValue(code, secure, cookieMaxAgeSeconds())
}

function readCapturedAtFromLocalStorage(): string | null {
  const storage = safeGetLocalStorage()
  if (!storage) return null
  const raw = storage.getItem(REFERRAL_LS_CAPTURED_AT_KEY)?.trim()
  return raw || null
}

function writeCapturedAtToLocalStorage(iso: string): void {
  const storage = safeGetLocalStorage()
  if (!storage) return
  storage.setItem(REFERRAL_LS_CAPTURED_AT_KEY, iso)
}

function readFromLocalStorage(): string | null {
  const storage = safeGetLocalStorage()
  if (!storage) return null
  return normalizeReferralCode(storage.getItem(REFERRAL_LS_KEY))
}

function writeToLocalStorage(code: string): void {
  const storage = safeGetLocalStorage()
  if (!storage) return
  storage.setItem(REFERRAL_LS_KEY, code)
}

function readFromUrl(): string | null {
  if (!isBrowser()) return null
  try {
    const url = new URL(window.location.href)
    return normalizeReferralCode(url.searchParams.get(REFERRAL_CODE_PARAM))
  } catch {
    return null
  }
}

/**
 * Último clique em `?ref=` antes do signup: sobrescreve código e reinicia a janela de 30 dias.
 * Sem `?ref=` na URL, reutiliza cookie/localStorage existentes.
 */
export function captureReferralFromPage(): void {
  if (!isBrowser()) return

  const fromUrl = readFromUrl()
  if (fromUrl) {
    writeToLocalStorage(fromUrl)
    writeCapturedAtToLocalStorage(new Date().toISOString())
    persistReferralCodeCookie(fromUrl)
    return
  }

  const fromLs = readFromLocalStorage()
  const fromCookie = readReferralCodeFromDocumentCookie(document.cookie)
  const existing = fromLs ?? fromCookie
  if (existing) {
    writeToLocalStorage(existing)
    persistReferralCodeCookie(existing)
  }
}

export function getReferralCode(): string | null {
  if (!isBrowser()) return null
  return resolveReferralCodeAtSignup({
    urlCode: readFromUrl(),
    localStorageCode: readFromLocalStorage(),
    cookieCode: readReferralCodeFromDocumentCookie(document.cookie),
  }).code
}

/** ISO 8601 para validação da janela de atribuição no signup. */
export function getReferralCapturedAt(): string | null {
  if (!isBrowser()) return null
  const urlCode = readFromUrl()
  return resolveReferralCapturedAtAtSignup({
    hasUrlCode: Boolean(urlCode),
    localStorageCapturedAt: readCapturedAtFromLocalStorage(),
  })
}

export function initReferralCapture(): void {
  captureReferralFromPage()
}
